import React from "react";
import { ColDef } from "ag-grid-community";

export const getActiveAdditionalFields = (additionalFields: any) => {
  if (!additionalFields) return [];

  const activeFields: Array<{
    index: number;
    name: string;
    type: string;
    choices?: string[];
  }> = [];

  for (let i = 1; i <= 10; i++) {
    const fieldName =
      additionalFields[`fieldName${i}` as keyof typeof additionalFields];
    const fieldType =
      additionalFields[`fieldType${i}` as keyof typeof additionalFields];
    const fieldChoices =
      additionalFields[`fieldChoices${i}` as keyof typeof additionalFields];

    if (fieldName && fieldType) {
      activeFields.push({
        index: i,
        name: fieldName as string,
        type: fieldType as string,
        choices: fieldChoices as string[] | undefined,
      });
    }
  }

  return activeFields;
};

interface AdditionalFieldColumnParams {
  additionalFields: any;
  changedCells: { [key: number]: Set<string> };
  setChangedCells: React.Dispatch<
    React.SetStateAction<{ [key: number]: Set<string> }>
  >;
  setGridDateValue: (params: any, field: string, value: string | null) => void;
  markCellChanged: (rowId: number, field?: string) => void;
  formatDateForInput: (dateString?: string | null) => string;
  openNativeDatePicker: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  stopGridMouseEvent: (event: React.SyntheticEvent) => void;
}

export const buildAdditionalFieldColumns = ({
  additionalFields,
  changedCells,
  setChangedCells,
  setGridDateValue,
  markCellChanged,
  formatDateForInput,
  openNativeDatePicker,
  stopGridMouseEvent,
}: AdditionalFieldColumnParams): ColDef[] => {
  const activeFields = getActiveAdditionalFields(additionalFields);

  return activeFields.map((field) => {
    const fieldKey = `fieldValue${field.index}`;

    return {
      field: fieldKey,
      headerName: field.name,
      editable:
        field.type !== "select" &&
        field.type !== "checkbox" &&
        field.type !== "date",
      filter: true,
      width: 180,
      cellClassRules: {
        "cell-changed": (params: any) =>
          params.data && changedCells[params.data.id]?.has(fieldKey),
      },
      cellRenderer: (params: any) => {
        if (!params.data) return null;
        if (field.type === "select" && field.choices) {
          const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            const updatedValue = e.target.value || null;
            params.setValue(updatedValue);

            setChangedCells((prev) => {
              const newChanges = { ...prev };
              if (!newChanges[params.data.id]) {
                newChanges[params.data.id] = new Set();
              }
              newChanges[params.data.id].add(fieldKey);
              return newChanges;
            });
          };

          return (
            <select
              value={params.value || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "5px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              <option value="">Seleccione una opción</option>
              {field.choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          );
        } else if (field.type === "checkbox") {
          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const updatedValue = e.target.checked ? "true" : "false";
            params.setValue(updatedValue);

            setChangedCells((prev) => {
              const newChanges = { ...prev };
              if (!newChanges[params.data.id]) {
                newChanges[params.data.id] = new Set();
              }
              newChanges[params.data.id].add(fieldKey);
              return newChanges;
            });
          };

          return (
            <input
              type="checkbox"
              checked={params.value === "true" || params.value === true}
              onChange={handleChange}
              style={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
              }}
            />
          );
        } else if (field.type === "date") {
          const handleDateChange = (
            e: React.ChangeEvent<HTMLInputElement>,
          ) => {
            const updatedValue = e.target.value || null;
            setGridDateValue(params, fieldKey, updatedValue);
            markCellChanged(params.data.id, fieldKey);
          };

          return (
            <input
              type="date"
              defaultValue={formatDateForInput(params.value)}
              onChange={handleDateChange}
              onClick={openNativeDatePicker}
              onFocus={openNativeDatePicker}
              onMouseDown={stopGridMouseEvent}
              onKeyDown={stopGridMouseEvent}
              style={{
                width: "100%",
                padding: "0px",
                border: "0px solid #ccc",
                borderRadius: "0px",
                backgroundColor: "transparent",
              }}
            />
          );
        } else if (field.type === "textarea") {
          return (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {params.value || ""}
            </div>
          );
        }

        return params.value || "";
      },
      cellEditor:
        field.type === "number" ? "agNumberCellEditor" : "agTextCellEditor",
      cellEditorParams:
        field.type === "number" ? { precision: 2 } : undefined,
    } as ColDef;
  });
};
